// ./src/pages/privates/Financial/index.tsx
import React, { useState, useCallback, useEffect } from 'react';

import {
  IoWarning,
  IoTrophy,
  IoRemoveCircle,
  IoReload,
  IoWallet,
} from 'react-icons/io5';
import { FaHandHoldingUsd } from 'react-icons/fa';

import {
  CardSimpleEstimate,
  HeaderNavigationPrivate,
  MenuSideBarPrivate,
  SubtitleSecondary,
  ButtonOutline,
} from '@components/index';
import { ConfigRoutes, ConfigStyles, ConfigValues } from '@config/index';
import { apiRebox } from '@services/index';
import { formatText } from '@utils/formatters';

import {
  Container,
  ContainerGroup,
  Content,
  Estimates,
  ButtonsOption,
} from './styles';

const Financial: React.FC = () => {
  const [totalChargesOverdue, setTotalChargesOverdue] = useState<number>(0);
  const [totalChargesGenerated, setTotalChargeGenerated] = useState<number>(0);
  const [totalChargesDeleted, setTotalChargesDeleted] = useState<number>(0);
  const [totalChargesProvider, setTotalChargesProvider] = useState<number>(0);
  const [totalRescuesGenerated, setTotalRescuesGenerated] = useState<number>(0);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const getChargesGenerated = useCallback(async () => {
    try {
      let url = '/payments?per_page=1';
      url += `&what_is_being_paid=${ConfigValues.rebox.payments.what_is_being_paid.contracts}`;
      const { data: response } = await apiRebox.get(url);
      const { header } = response;
      setTotalChargeGenerated(header.total);
    } catch (error) {
      console.error(
        'Error ao tentar buscar total de cobranças atrasadas: ',
        error,
      );
    }
  }, []);

  const getChargesOverdue = useCallback(async () => {
    try {
      let url = '/payments?per_page=1';
      url += `&what_is_being_paid=${ConfigValues.rebox.payments.what_is_being_paid.contracts}`;
      url += `&status=${ConfigValues.rebox.payments.status.overdue}`;
      const { data: response } = await apiRebox.get(url);
      const { header } = response;
      setTotalChargesOverdue(header.total);
    } catch (error) {
      console.error(
        'Error ao tentar buscar total de cobranças atrasadas: ',
        error,
      );
    }
  }, []);

  const getChargesDeleted = useCallback(async () => {
    try {
      let url = '/payments?per_page=1';
      url += `&what_is_being_paid=${ConfigValues.rebox.payments.what_is_being_paid.contracts}`;
      url += `&status=${ConfigValues.rebox.payments.status.canceled}`;
      const { data: response } = await apiRebox.get(url);
      const { header } = response;
      setTotalChargesDeleted(header.total);
    } catch (error) {
      console.error(
        'Error ao tentar buscar total de cobranças deletadas: ',
        error,
      );
    } finally {
      setLoading(prevState => !prevState);
    }
  }, []);

  const getRescues = useCallback(async () => {
    try {
      const url = '/rescues?details=all';
      const { data: response } = await apiRebox.get(url);
      const { header } = response;
      setTotalRescuesGenerated(header.total);
    } catch (error) {
      console.error('Error ao tentar buscar total de resgates: ', error);
    }
  }, []);

  const getChargesProvider = useCallback(async () => {
    try {
      let url = '/called';
      url += `?status=${ConfigValues.rebox.called.status.done}`;
      const { data: response } = await apiRebox.get(url);
      const { header } = response;
      setTotalChargesProvider(header.total);
    } catch (error) {
      console.error(
        'Error ao tentar buscar total de cobrancas prestadores ',
        error,
      );
    }
  }, []);

  useEffect(() => {
    setLoading(prevState => !prevState);
    getChargesGenerated();
    getChargesOverdue();
    getChargesDeleted();
    getRescues();
    getChargesProvider();
  }, [refresh]);

  return (
    <Container>
      <HeaderNavigationPrivate />
      <ContainerGroup>
        <MenuSideBarPrivate />
        <Content>
          <SubtitleSecondary textAlign="start">Financeiro</SubtitleSecondary>
          <ButtonsOption>
            <ButtonOutline
              className="btn-update"
              loading={loading}
              iconLeft={IoReload}
              onClick={() => setRefresh(prevState => !prevState)}
              style={{ minWidth: '140px' }}
            >
              Atualizar
            </ButtonOutline>
          </ButtonsOption>
          <Estimates>
            <CardSimpleEstimate
              icon={IoTrophy}
              iconColor={ConfigStyles.rebox.colors.greenSea.main}
              iconSize={28}
              iconOpacity={0.8}
              label={`Cobranças geradas`}
              value={formatText.numberSeparatedByThousand(
                totalChargesGenerated,
              )}
              linkName={`Listar cobranças`}
              route={ConfigRoutes.rebox.privates.financial.next.charges.path}
            />
            <CardSimpleEstimate
              icon={IoWarning}
              iconColor={ConfigStyles.rebox.colors.yellowRisenShine.main}
              iconSize={28}
              iconOpacity={0.8}
              label={`Cobranças em atraso`}
              value={formatText.numberSeparatedByThousand(totalChargesOverdue)}
              linkName={`Listar cobranças`}
              route={ConfigRoutes.rebox.privates.financial.next.charges.path}
            />
            <CardSimpleEstimate
              icon={IoRemoveCircle}
              iconColor={ConfigStyles.rebox.colors.gray.main}
              iconSize={28}
              iconOpacity={0.8}
              label={`Cobranças canceladas`}
              value={formatText.numberSeparatedByThousand(totalChargesDeleted)}
              linkName={`Listar cobranças`}
              route={ConfigRoutes.rebox.privates.financial.next.charges.path}
            />
            <CardSimpleEstimate
              icon={FaHandHoldingUsd}
              iconColor={ConfigStyles.rebox.colors.purple.main}
              iconSize={28}
              iconOpacity={0.8}
              label={`Cobranças de prestadores`}
              value={formatText.numberSeparatedByThousand(totalChargesProvider)}
              linkName={`Listar cobrancas`}
              route={
                ConfigRoutes.rebox.privates.financial.next.calledProviders.path
              }
            />
            <CardSimpleEstimate
              icon={IoWallet}
              iconColor={ConfigStyles.rebox.colors.gray.main}
              iconSize={28}
              iconOpacity={0.8}
              label={`Resgates solicitados`}
              value={formatText.numberSeparatedByThousand(
                totalRescuesGenerated,
              )}
              linkName={`Listar resgates`}
              route={
                ConfigRoutes.rebox.privates.financial.next.commissions.path
              }
            />
          </Estimates>
        </Content>
      </ContainerGroup>
    </Container>
  );
};

export default Financial;
